/**
 * CodigoApproval - Sistema de aprobación de códigos de ética
 * Features:
 * - Aprobar código
 * - Rechazar con comentarios
 * - Vista previa del código
 */
import { useState } from 'react';
import { CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { codigosAPI } from '../../api';
import { Button, Alert } from '../common';
import useAuthStore from '../../store/authStore';

const CodigoApproval = ({ codigoId, onSuccess, onCancel }) => {
  const { user } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comentarios, setComentarios] = useState('');
  const [accion, setAccion] = useState(null); // 'aprobar' | 'rechazar'

  const handleAprobar = async () => {
    try {
      setLoading(true);
      setError(null);

      await codigosAPI.aprobar(codigoId, {
        aprobado: true,
        comentarios: comentarios,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error al aprobar código:', err);
      setError(err.message || 'Error al aprobar código');
    } finally {
      setLoading(false);
    }
  };

  const handleRechazar = async () => {
    if (!comentarios.trim()) {
      setError('Debes proporcionar comentarios al rechazar');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await codigosAPI.aprobar(codigoId, {
        aprobado: false,
        comentarios: comentarios,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error al rechazar código:', err);
      setError(err.message || 'Error al rechazar código');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <Alert type="error" title="Error" message={error} onClose={() => setError(null)} />
      )}

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
        <p className="text-sm text-blue-900">
          <strong>Rol:</strong> {user?.rol === 'superadmin' ? 'Super Admin' : 'Admin de Organización'}
        </p>
        <p className="text-sm text-blue-900 mt-1">
          Como miembro del comité de ética, puedes aprobar o rechazar este código.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Comentarios {accion === 'rechazar' && <span className="text-red-600">*</span>}
        </label>
        <textarea
          value={comentarios}
          onChange={(e) => setComentarios(e.target.value)}
          rows={4}
          placeholder="Agrega tus comentarios sobre el código de ética..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        {accion === 'rechazar' && !comentarios.trim() && (
          <p className="text-sm text-red-600 mt-1">
            Los comentarios son obligatorios al rechazar
          </p>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Efectos de la Aprobación</h4>
        <ul className="space-y-1 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>El código pasará de "Borrador" a "Activo"</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>Estará disponible para toda la organización</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>Se registrará tu aprobación en el historial</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-3 justify-end pt-4 border-t">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}

        <Button
          variant="danger"
          onClick={() => {
            setAccion('rechazar');
            handleRechazar();
          }}
          disabled={loading}
          icon={XCircle}
        >
          Rechazar
        </Button>

        <Button
          variant="primary"
          onClick={() => {
            setAccion('aprobar');
            handleAprobar();
          }}
          disabled={loading}
          loading={loading}
          icon={CheckCircle}
        >
          Aprobar Código
        </Button>
      </div>
    </div>
  );
};

export default CodigoApproval;
